import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UpdateRentingDto } from '../api/rentingsApi';
import Button from '@/components/ui/button/Button';
import Label from '@/components/form/Label';
import Input from '@/components/form/input/InputField';
import Select from '@/components/form/Select';
import ReactDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { withFormProtection } from '@/components/form/withFormProtection';
import { useToast } from '@/components/ui/toast/Toast';
import { Client } from '@/features/clients/api/clientsApi';
import { Car } from '../api/rentingsApi';

interface RentingEditFormProps {
  rentingId: string;
  onSubmit: (data: UpdateRentingDto) => Promise<void>;
  onClose: () => void;
  initialData?: UpdateRentingDto;
  currentData?: UpdateRentingDto;
  onDataChange?: (data: UpdateRentingDto) => void;
  client: Client;
  car: Car;
}

const defaultFormData: UpdateRentingDto = {
  start_date: '',
  start_time: '',
  end_date: '',
  end_time: '',
  address_to_deliver: '',
  address_to_pickup: '',
  deliver_latitude: 0,
  deliver_longitude: 0,
  pickup_latitude: 0,
  pickup_longitude: 0,
  extra_services: [],
};

const RentingEditForm = React.forwardRef<{ handleClose: () => void; isDirty: () => boolean }, RentingEditFormProps>(({
  rentingId,
  onSubmit,
  onClose,
  initialData,
  onDataChange,
  client,
  car,
}, ref) => {
  const { t } = useTranslation();
  const toast = useToast();
  const [formData, setFormData] = useState<UpdateRentingDto>(initialData || defaultFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Обновляем состояние когда меняются props
  React.useEffect(() => {
    if (initialData) {
      setFormData(initialData);
    }
  }, [initialData]);

  // Notify parent about form data changes
  const handleFormDataChange = useCallback((newData: UpdateRentingDto) => {
    onDataChange?.(newData);
  }, [onDataChange]);

  const validateForm = (data: UpdateRentingDto): boolean => {
    const newErrors: Record<string, string> = {};

    if (!data.start_date) {
      newErrors.start_date = t('rentings.edit.errors.start_date_required');
    }
    if (!data.start_time) {
      newErrors.start_time = t('rentings.edit.errors.start_time_required');
    }
    if (!data.end_date) {
      newErrors.end_date = t('rentings.edit.errors.end_date_required');
    }
    if (!data.end_time) {
      newErrors.end_time = t('rentings.edit.errors.end_time_required');
    }
    if (!data.address_to_deliver.trim()) {
      newErrors.address_to_deliver = t('rentings.edit.errors.delivery_address_required');
    }
    if (!data.address_to_pickup.trim()) {
      newErrors.address_to_pickup = t('rentings.edit.errors.pickup_address_required');
    }


    // Validate that end date/time is after start date/time
    if (data.start_date && data.start_time && data.end_date && data.end_time) {
      const startDateTime = new Date(`${data.start_date}T${data.start_time}`);
      const endDateTime = new Date(`${data.end_date}T${data.end_time}`);

      if (endDateTime <= startDateTime) {
        newErrors.end_date = t('rentings.edit.errors.end_after_start_required');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const clearFieldError = (field: string) => {
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleStartDateChange = (selectedDate: Date | null) => {
    const newDate = selectedDate ? selectedDate.toISOString().split('T')[0] : '';
    const newData = { ...formData, start_date: newDate };
    setFormData(newData);
    handleFormDataChange(newData);
    clearFieldError('start_date');
    validateForm(newData);
  };

  const handleEndDateChange = (selectedDate: Date | null) => {
    const newDate = selectedDate ? selectedDate.toISOString().split('T')[0] : '';
    const newData = { ...formData, end_date: newDate };
    setFormData(newData);
    handleFormDataChange(newData);
    clearFieldError('end_date');
    validateForm(newData);
  };

  const handleStartTimeChange = (value: string) => {
    const newData = { ...formData, start_time: value };
    setFormData(newData);
    handleFormDataChange(newData);
    clearFieldError('start_time');
    validateForm(newData);
  };

  const handleEndTimeChange = (value: string) => {
    const newData = { ...formData, end_time: value };
    setFormData(newData);
    handleFormDataChange(newData);
    clearFieldError('end_time');
    validateForm(newData);
  };

  const handleDeliveryAddressChange = (value: string) => {
    const newData = { ...formData, address_to_deliver: value };
    setFormData(newData);
    handleFormDataChange(newData);
    clearFieldError('address_to_deliver');
  };

  const handlePickupAddressChange = (value: string) => {
    const newData = { ...formData, address_to_pickup: value };
    setFormData(newData);
    handleFormDataChange(newData);
    clearFieldError('address_to_pickup');
  };

  const handleDeliveryLatitudeChange = (value: string) => {
    const newData = { ...formData, deliver_latitude: value ? parseFloat(value) : 0 };
    setFormData(newData);
    handleFormDataChange(newData);
  };

  const handleDeliveryLongitudeChange = (value: string) => {
    const newData = { ...formData, deliver_longitude: value ? parseFloat(value) : 0 };
    setFormData(newData);
    handleFormDataChange(newData);
  };

  const handlePickupLatitudeChange = (value: string) => {
    const newData = { ...formData, pickup_latitude: value ? parseFloat(value) : 0 };
    setFormData(newData);
    handleFormDataChange(newData);
  };

  const handlePickupLongitudeChange = (value: string) => {
    const newData = { ...formData, pickup_longitude: value ? parseFloat(value) : 0 };
    setFormData(newData);
    handleFormDataChange(newData);
  };



  const handleSubmit = async () => {
    if (!validateForm(formData)) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
      onClose();
      toast.success(t('rentings.edit.success'));
    } catch (error) {
      toast.error(t('rentings.edit.error') + `<br/><br/>${error}`);
      setErrors({ submit: t('rentings.edit.errors.update_failed') });
    } finally {
      setIsSubmitting(false);
    }
  };



  // Forward the ref
  React.useImperativeHandle(ref, () => ({
    handleClose: onClose,
    isDirty: () => {
      if (!initialData) return false;
      return JSON.stringify(initialData) !== JSON.stringify(formData);
    },
  }), [onClose, initialData, formData]);

  return (
    <>
      <div className="px-6 py-4">
        <h5 className="text-xl font-semibold text-text-primary lg:text-2xl">
          {t('rentings.edit.title')}
        </h5>
        <p className="text-sm text-text-tertiary">
          {t('rentings.edit.description')}
        </p>
      </div>

      <div className="p-6">
        <div className="space-y-6">
          {/* Client and Car (disabled) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>{t('rentings.edit.fields.client')}</Label>
              <Input
                value={`${client.first_name} ${client.last_name}`}
                disabled
                className="bg-bg-tertiary"
              />
            </div>
            <div>
              <Label>{t('rentings.edit.fields.car')}</Label>
              <Input
                value={`${car.model.brand_name} ${car.model.model_name} ${car.registration_number}`}
                disabled
                className="bg-bg-tertiary"
              />
            </div>
          </div>

          {/* Date and Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>
                {t('rentings.edit.fields.start_date')}
                <span className="text-danger ml-1">*</span>
              </Label>
              <ReactDatePicker
                selected={formData.start_date ? new Date(formData.start_date) : null}
                onChange={handleStartDateChange}
                dateFormat="dd/MM/yyyy"
                className={`w-full rounded-lg ${errors.start_date ? 'border-error-500' : 'border-border-input'} bg-transparent py-[9px] px-4 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter bg-bg-input focus:border-primary`}
                placeholderText={t('rentings.edit.placeholders.select_date')}
              />
              {errors.start_date && (
                <p className="text-sm text-danger mt-1">{errors.start_date}</p>
              )}
            </div>
            <div>
              <Label>
                {t('rentings.edit.fields.start_time')}
                <span className="text-danger ml-1">*</span>
              </Label>
              <Input
                type="time"
                value={formData.start_time}
                onChange={(e) => handleStartTimeChange(e.target.value)}
                error={!!errors.start_time}
              />
              {errors.start_time && (
                <p className="text-sm text-danger mt-1">{errors.start_time}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>
                {t('rentings.edit.fields.end_date')}
                <span className="text-danger ml-1">*</span>
              </Label>
              <ReactDatePicker
                selected={formData.end_date ? new Date(formData.end_date) : null}
                onChange={handleEndDateChange}
                dateFormat="dd/MM/yyyy"
                className={`w-full rounded-lg ${errors.end_date ? 'border-error-500' : 'border-border-input'} bg-transparent py-[9px] px-4 outline-none transition focus:border-primary active:border-primary disabled:cursor-default disabled:bg-whiter bg-bg-input focus:border-primary`}
                placeholderText={t('rentings.edit.placeholders.select_date')}
              />
              {errors.end_date && (
                <p className="text-sm text-danger mt-1">{errors.end_date}</p>
              )}
            </div>
            <div>
              <Label>
                {t('rentings.edit.fields.end_time')}
                <span className="text-danger ml-1">*</span>
              </Label>
              <Input
                type="time"
                value={formData.end_time}
                onChange={(e) => handleEndTimeChange(e.target.value)}
                error={!!errors.end_time}
              />
              {errors.end_time && (
                <p className="text-sm text-danger mt-1">{errors.end_time}</p>
              )}
            </div>
          </div>

          {/* Addresses */}
          <div className="space-y-4">
            <div>
              <Label>
                {t('rentings.edit.fields.delivery_address')}
                <span className="text-danger ml-1">*</span>
              </Label>
              <Input
                value={formData.address_to_deliver}
                onChange={(e) => handleDeliveryAddressChange(e.target.value)}
                placeholder={t('rentings.edit.placeholders.enter_delivery_address')}
                error={!!errors.address_to_deliver}
              />
              {errors.address_to_deliver && (
                <p className="text-sm text-danger mt-1">{errors.address_to_deliver}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 hidden">
              <div>
                <Label>{t('rentings.edit.fields.delivery_latitude')}</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.deliver_latitude.toString()}
                  onChange={(e) => handleDeliveryLatitudeChange(e.target.value)}
                  placeholder={t('rentings.edit.placeholders.enter_latitude')}
                />
              </div>
              <div>
                <Label>{t('rentings.edit.fields.delivery_longitude')}</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.deliver_longitude.toString()}
                  onChange={(e) => handleDeliveryLongitudeChange(e.target.value)}
                  placeholder={t('rentings.edit.placeholders.enter_longitude')}
                />
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <Label>
                {t('rentings.edit.fields.pickup_address')}
                <span className="text-danger ml-1">*</span>
              </Label>
              <Input
                value={formData.address_to_pickup}
                onChange={(e) => handlePickupAddressChange(e.target.value)}
                placeholder={t('rentings.edit.placeholders.enter_pickup_address')}
                error={!!errors.address_to_pickup}
              />
              {errors.address_to_pickup && (
                <p className="text-sm text-danger mt-1">{errors.address_to_pickup}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 hidden">
              <div>
                <Label>{t('rentings.edit.fields.pickup_latitude')}</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.pickup_latitude.toString()}
                  onChange={(e) => handlePickupLatitudeChange(e.target.value)}
                  placeholder={t('rentings.edit.placeholders.enter_latitude')}
                />
              </div>
              <div>
                <Label>{t('rentings.edit.fields.pickup_longitude')}</Label>
                <Input
                  type="number"
                  step="any"
                  value={formData.pickup_longitude.toString()}
                  onChange={(e) => handlePickupLongitudeChange(e.target.value)}
                  placeholder={t('rentings.edit.placeholders.enter_longitude')}
                />
              </div>
            </div>
          </div>



          {errors.submit && (
            <div className="text-sm text-danger">{errors.submit}</div>
          )}
        </div>
      </div>

      <div className="px-6 py-4">
        <div className="flex items-center gap-3 sm:justify-end">
          <Button variant="outline" size="sm" onClick={onClose}>
            {t('common.buttons.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? t('rentings.edit.buttons.updating') : t('rentings.edit.buttons.update')}
          </Button>
        </div>
      </div>

      <style>
        {`
          .react-datepicker-wrapper {
            width: 100%;
          }

          .react-datepicker-popper {
            z-index: 20;
          }

          .react-datepicker__month-container {
            float: none !important;
            min-height: 310px !important;
          }

          /* Стили для мобильных устройств */
          @media (max-width: 767px) {
            .react-datepicker-popper {
              position: fixed !important;
              top: auto !important;
              bottom: 0 !important;
              left: 0 !important;
              right: 0 !important;
              transform: none !important;
              margin: 1rem !important;
              width: calc(100% - 2rem) !important;
            }

            .react-datepicker {
              width: 100% !important;
            }

            .react-datepicker__month-container {
              width: 100% !important;
              float: none !important;
            }

            .react-datepicker__day {
              width: calc((100% - 2.8rem) / 7) !important;
              line-height: 2.5rem !important;
              margin: 0.2rem !important;
            }

            .react-datepicker__day-name {
              width: calc((100% - 2.8rem) / 7) !important;
              margin: 0.2rem !important;
            }
          }

            @media (min-width: 768px) {
              .react-datepicker-wrapper {
                position: relative;
              }

              .react-datepicker-popper {
                z-index: 20;
                margin-top: 0.1rem !important;
              }

              .react-datepicker-popper[data-placement^='bottom-start'] {
                margin-left: 0rem !important;
              }

              .react-datepicker-popper[data-placement^='bottom-end'] {
                margin-right: 0.5rem !important;
              }
            }

            .react-datepicker-popper[data-placement^='bottom'] .react-datepicker__triangle {
              display: none;
            }

            .react-datepicker {
              font-family: inherit;
              border: 1px solid #e5e7eb !important;
              border-radius: 1rem;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
              font-size: 0.875rem;
            }

            .react-datepicker__header {
              background-color: transparent;
              border-bottom: 1px solid #e5e7eb;
              border-radius: 1rem 1rem 0 0;
              padding: 0.75rem 0;
            }

            .react-datepicker__current-month {
              font-weight: 600;
              font-size: 0.875rem;
              color: #1f2937;
              margin-bottom: 0.5rem;
            }

            .react-datepicker__day-name {
              color: #6b7280;
              font-weight: 500;
              font-size: 0.75rem;
              width: 2rem;
              margin: 0.2rem;
            }

            .react-datepicker__day {
              color: #1f2937;
              border-radius: 0.5rem;
              margin: 0.2rem;
              width: 2rem;
              line-height: 2rem;
              font-size: 0.875rem;
            }

            .react-datepicker__day:hover {
              background-color: #f3f4f6;
              border-radius: 0.5rem;
            }

            .react-datepicker__day--selected,
            .react-datepicker__day--keyboard-selected {
              background-color: #3b82f6 !important;
              border-radius: 0.5rem;
              color: white !important;
            }

            .react-datepicker__day--disabled {
              color: #9ca3af;
            }

            .react-datepicker__navigation {
              top: 0.75rem;
            }

            .react-datepicker__navigation-icon::before {
              border-color: #6b7280;
              border-width: 2px 2px 0 0;
              height: 8px;
              width: 8px;
            }

            .dark .react-datepicker {
              background-color: #1e293b;
              border-color: #334155 !important;
            }

            .dark .react-datepicker__header {
              background-color: #1e293b;
              border-color: #334155;
            }

            .dark .react-datepicker__current-month {
              color: #e5e7eb;
            }

            .dark .react-datepicker__day-name {
              color: #9ca3af;
            }

            .dark .react-datepicker__day {
              color: #e5e7eb;
            }

            .dark .react-datepicker__day:hover {
              background-color: #334155;
            }

            .dark .react-datepicker__day--disabled {
              color: #4b5563;
            }

            .dark .react-datepicker__navigation-icon::before {
              border-color: #9ca3af;
            }
        `}
      </style>
    </>
  );
});

RentingEditForm.displayName = 'RentingEditForm';

const ProtectedRentingEditForm = withFormProtection(RentingEditForm);

interface RentingEditFormWrapperProps extends Omit<RentingEditFormProps, 'currentData' | 'onDataChange' | 'initialData'> {
  ref?: React.Ref<{ handleClose: () => void; isDirty: () => boolean }>;
  initialData?: UpdateRentingDto;
}

const RentingEditFormWrapper = React.forwardRef<{ handleClose: () => void; isDirty: () => boolean }, RentingEditFormWrapperProps>((props, ref) => {
  const initialData = props.initialData || defaultFormData;

  const [currentData, setCurrentData] = useState<UpdateRentingDto>(initialData);

  const handleDataChange = useCallback((data: UpdateRentingDto) => {
    setCurrentData(data);
  }, []);

  return (
    <ProtectedRentingEditForm
      {...props}
      ref={ref}
      initialData={initialData}
      currentData={currentData}
      onDataChange={handleDataChange}
    />
  );
});

RentingEditFormWrapper.displayName = 'RentingEditFormWrapper';

export default RentingEditFormWrapper;
